import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import logo from "./assets/itiniri_app_logo.png";
import shadowLogo from "./assets/itiniri_shadow.png";
import Support from "./Pages/Support";
import Privacy from "./Pages/Privacy";
import Demo from "./Pages/Demo";
import Calculator from "./components/Calculator";
import ArtistInstructions from "./Pages/FAQ_pages/ArtistInstructions";
import VendorInstructions from "./Pages/FAQ_pages/VendorInstructions";
import GatheringInstructions from "./Pages/FAQ_pages/GatheringInstructions";
import ReferralInstructions from "./Pages/FAQ_pages/ReferralInstructions";
import FAQ from "./Pages/FAQ";

import screenshot1 from "./assets/home-screen.png";
import screenshot2 from "./assets/schedule.png";
import screenshot3 from "./assets/map.png";
import screenshot4 from "./assets/vendor.png";

import ScrollToTop from "./helpers/scrollToTop";

function Home() {
  const navigate = useNavigate();

  return (
    <section className="hero-section">
      <div id="hero-content" className="hero-content">
        <div className="hero-title-wrapper">
          <h1 className="main-title">
            Affordable Event App for Festivals, Conferences & Live Events
          </h1>
        </div>

        <p className="sub-title">
          A powerful, easy-to-use event app with schedules, maps, offline
          access, and push notifications—at a fraction of the cost of Sched,
          Whova, and Guidebook.
        </p>

        <div className="cta-buttons">
          <a
            href="https://itiniri.app/auth"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="button get-started">Get Started</button>
          </a>

          <h4 id="or">OR</h4>

          <button
            className="button view-demo body-demo"
            onClick={() => navigate("/demo")}
          >
            View Demo
          </button>
        </div>
      </div>

      {/* INTRO */}
      <div id="intro-container" className="intro-container">
        <h2>The Best Low-Cost Event App Alternative</h2>
        <p>
          Itiniri is a modern event app designed for festivals, conferences, and
          live events that need powerful features without the high price tag.
          Manage schedules, maps, vendors, and announcements in one simple
          platform your attendees can access anytime.
        </p>
      </div>

      {/* VALUE PROPS */}
      <div id="steps-container" className="steps-container">
        <div className="step">
          <h3>Save Time</h3>
          <p>
            Manage your entire event program, schedule, and updates in one
            centralized event app.
          </p>
        </div>

        <div className="step">
          <h3>Save Thousands</h3>
          <p>
            Unlike expensive platforms like Sched, Whova, and Guidebook, Itiniri
            offers the same core features at a fraction of the cost.
          </p>
        </div>

        <div className="step">
          <h3>Built for Real Events</h3>
          <p>
            Perfect for festivals, outdoor events, and conferences where
            simplicity and reliability matter most.
          </p>
        </div>
      </div>

      {/* FEATURES */}
      <div className="features-section">
        <h2 className="features-heading">
          Event App Features for Festivals, Conferences, and Live Events
        </h2>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">📱</div>
            <h3>Offline Event App Access</h3>
            <p>
              Attendees can access schedules, maps, and event information even
              without an internet connection.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🔔</div>
            <h3>Push Notifications</h3>
            <p>
              Send real-time updates for schedule changes, emergencies, or
              announcements.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h3>Live Event Updates</h3>
            <p>
              Instantly update event details and notify attendees across your
              entire event app.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🤝</div>
            <h3>Vendor & Artist Integration</h3>
            <p>
              Showcase vendors, artists, and sponsors with built-in profiles and
              links.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">💝</div>
            <h3>Free for Attendees</h3>
            <p>
              No cost for users, vendors, or artists—making adoption seamless for
              your entire event.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🌙</div>
            <h3>Dark & Light Mode</h3>
            <p>
              Users can customize their experience with multiple viewing modes.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">⭐</div>
            <h3>Personalized Event Schedules</h3>
            <p>
              Attendees can create their own schedule and favorite events.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🗺️</div>
            <h3>Interactive Event Maps</h3>
            <p>
              Provide high-quality maps to help attendees navigate your event
              easily.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📲</div>
            <h3>Social Integration</h3>
            <p>
              Connect attendees with vendors, artists, and sponsors through
              social links.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">💭</div>
            <h3>Custom Event Features</h3>
            <p>
              Need something specific? We can work with you to build custom
              features for your event.
            </p>
          </div>
        </div>
      </div>

      <div className="screenshots">
        <div className="screenshots-grid">
          <img
            src={screenshot1}
            alt="Itiniri screenshot 1"
            className="screenshot-item"
          />
          <img
            src={screenshot2}
            alt="Itiniri screenshot 2"
            className="screenshot-item"
          />
          <img
            src={screenshot3}
            alt="Itiniri screenshot 3"
            className="screenshot-item"
          />
          <img
            src={screenshot4}
            alt="Itiniri screenshot 4"
            className="screenshot-item"
          />
        </div>
      </div>

      {/* PRICING POSITIONING */}
      <div className="pricing-positioning">
        <h2>Save Thousands on Your Event App</h2>
        <p>
          Traditional event apps can cost thousands—or even tens of thousands of
          dollars. Itiniri provides the same essential functionality—schedules,
          maps, and notifications—without the inflated price.
        </p>
      </div>

      {/* CALCULATOR */}
      <div id="calculator-section">
        <Calculator />
      </div>

      {/* FAQ PREVIEW FOR SEO */}
      <div className="faq-preview">
        <h2>Event App FAQs</h2>

        <h3>What is an event app?</h3>
        <p>
          An event app is a mobile platform that allows attendees to view
          schedules, maps, vendors, and live updates for festivals, conferences,
          and events.
        </p>

        <h3>How much does an event app cost?</h3>
        <p>
          Many event apps cost thousands of dollars. Itiniri offers a much more
          affordable alternative with the same core features.
        </p>

        <h3>Does Itiniri work offline?</h3>
        <p>
          Yes. After initial setup, attendees can access key event information
          without an internet connection.
        </p>
      </div>
    </section>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />

      <div className="App">
        <header id="header" className="header">
          <a href="/">
            <img src={logo} alt="Itiniri Event App Logo" className="logo-image" />
          </a>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              className="button header-demo-btn"
              onClick={() => (window.location.href = "/faq")}
            >
              FAQ
            </button>

            <button
              className="button header-demo-btn"
              onClick={() => (window.location.href = "/demo")}
            >
              View Demo
            </button>

            <a
              href="https://itiniri.app/auth"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="button get-started">Get Started</button>
            </a>
          </div>
        </header>

        <div className="content-wrapper">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/support" element={<Support />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/faq/vendors" element={<VendorInstructions />} />
            <Route path="/faq/artists" element={<ArtistInstructions />} />
            <Route path="/faq/events" element={<GatheringInstructions />} />
            <Route path="/faq/referral" element={<ReferralInstructions />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>

          <footer className="footer">
            <div className="footer-links">
              <a href="/support" className="footer-link">
                Support
              </a>
              <a href="/privacy" className="footer-link">
                Privacy
              </a>
              <a href="/demo" className="footer-link">
                Demo
              </a>
            </div>

            <div className="copyright">
              © 2026 Itiniri | All rights reserved
            </div>
          </footer>
        </div>
      </div>
    </Router>
  );
}

export default App;